import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Alert, Form, Spinner } from 'react-bootstrap';
import Icon from '../../common/Icons';
import ClinicalButton from '../../doctor/ui/ClinicalButton';
import consultationService from '../../../services/consultationService';
import nursingCareCatalogService from '../../../services/nursingCareCatalogService';
import nursingInjectionCatalogService from '../../../services/nursingInjectionCatalogService';
import familyPlanningCatalogService from '../../../services/familyPlanningCatalogService';
import { NURSE_COPY } from '../nurseCopy';
import { NurseWorkspaceContext } from '../nurseWorkspaceContext';
import {
  catalogItemKey,
  isFamilyPlanningCatalogItemRecorded,
  isInjectionCatalogItemRecorded,
  isManualCareAction,
  recordedCareCatalogIds,
  recordedSelectionForKind
} from '../utils/nurseCareActRecording';

function StatPill({ label, value, tone = 'default' }) {
  return (
    <div className={`nr-care-stat nr-care-stat--${tone}`}>
      <span className="nr-care-stat__value">{value}</span>
      <span className="nr-care-stat__label">{label}</span>
    </div>
  );
}

async function loadCatalog(kind) {
  if (kind === 'injection') return nursingInjectionCatalogService.list({ is_active: 'true' });
  if (kind === 'family_planning') return familyPlanningCatalogService.list({ is_active: 'true' });
  return nursingCareCatalogService.list({ is_active: 'true' });
}

function isItemRecorded(kind, item, careActions, recordedIds) {
  if (kind === 'injection') return isInjectionCatalogItemRecorded(item, careActions);
  if (kind === 'family_planning') return isFamilyPlanningCatalogItemRecorded(item, careActions);
  return recordedIds.has(String(item.id));
}

function savePayload(kind, item, note) {
  if (kind === 'injection') {
    return { injection_catalog_item_id: item.id, treatment_given: note };
  }
  if (kind === 'family_planning') {
    return { family_planning_catalog_item_id: item.id, treatment_given: note };
  }
  return {
    catalog_item_id: item.id,
    action_type: item.name,
    description: item.name,
    treatment_given: note
  };
}

export default function NurseCareChecklist({
  consultationId,
  toast,
  timeline,
  onTimelineRefresh,
  kind = 'care'
}) {
  const workspace = useContext(NurseWorkspaceContext);
  const C = workspace?.copy?.CARE_CHECKLIST || NURSE_COPY.CARE_CHECKLIST;
  const K = { ...C, ...(C.KINDS?.[kind] || {}) };
  const [catalog, setCatalog] = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [busy, setBusy] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [globalNote, setGlobalNote] = useState('');
  const [viewFilter, setViewFilter] = useState('ALL');

  const careActions = timeline?.careActions;
  const recordedIds = useMemo(() => recordedCareCatalogIds(careActions), [careActions]);
  const manualCareActions = useMemo(
    () => (kind === 'care' ? (careActions || []).filter(isManualCareAction) : []),
    [kind, careActions]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingCatalog(true);
      try {
        const res = await loadCatalog(kind);
        if (!cancelled) {
          setCatalog(res.success && Array.isArray(res.data) ? res.data : []);
        }
      } catch {
        if (!cancelled) setCatalog([]);
      } finally {
        if (!cancelled) setLoadingCatalog(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [kind]);

  const recordedSelectionKey = useMemo(
    () => [...recordedSelectionForKind(kind, careActions, catalog)].sort().join(','),
    [kind, careActions, catalog]
  );

  useEffect(() => {
    setSelectedIds(new Set(recordedSelectionKey ? recordedSelectionKey.split(',') : []));
  }, [recordedSelectionKey]);

  const sortedCatalog = useMemo(
    () =>
      [...catalog].sort(
        (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.name.localeCompare(b.name, 'fr')
      ),
    [catalog]
  );

  const visibleItems = useMemo(() => {
    return sortedCatalog.filter((item) => {
      const key = catalogItemKey(kind, item.id);
      if (viewFilter === 'CHECKED') return selectedIds.has(key);
      if (viewFilter === 'PENDING') {
        return selectedIds.has(key) && !isItemRecorded(kind, item, careActions, recordedIds);
      }
      return true;
    });
  }, [sortedCatalog, viewFilter, selectedIds, kind, careActions, recordedIds]);

  const toggleItem = useCallback((key) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const selectAllVisible = useCallback(() => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      visibleItems.forEach((item) => next.add(catalogItemKey(kind, item.id)));
      return next;
    });
  }, [visibleItems, kind]);

  const clearNewSelection = useCallback(() => {
    setSelectedIds(recordedSelectionForKind(kind, careActions, catalog));
  }, [kind, careActions, catalog]);

  const pendingItems = useMemo(
    () =>
      sortedCatalog.filter((item) => {
        const key = catalogItemKey(kind, item.id);
        return selectedIds.has(key) && !isItemRecorded(kind, item, careActions, recordedIds);
      }),
    [sortedCatalog, selectedIds, kind, careActions, recordedIds]
  );

  const recordedCount = useMemo(
    () => sortedCatalog.filter((item) => isItemRecorded(kind, item, careActions, recordedIds)).length,
    [sortedCatalog, kind, careActions, recordedIds]
  );

  const handleSave = async (e) => {
    e.preventDefault();
    if (!pendingItems.length) {
      toast('warning', K.NOTHING_NEW);
      return;
    }
    setBusy(true);
    try {
      const note = globalNote.trim() || null;
      for (const item of pendingItems) {
        const res = await consultationService.addNursingCareAction(
          consultationId,
          savePayload(kind, item, note)
        );
        if (!res.success) {
          throw new Error(res.message || `Enregistrement impossible pour ${item.name}`);
        }
      }
      toast(
        'success',
        pendingItems.length === 1 ? K.SAVED_ONE : K.SAVED_MANY.replace('{n}', String(pendingItems.length))
      );
      setGlobalNote('');
      await onTimelineRefresh?.();
    } catch (err) {
      toast('danger', err.message || K.SAVE_ERROR);
    } finally {
      setBusy(false);
    }
  };

  if (loadingCatalog) {
    return (
      <div className="nr-care-board nr-care-board--loading">
        <Spinner animation="border" size="sm" variant="success" />
        <p className="text-muted small mt-2 mb-0">{K.LOADING}</p>
      </div>
    );
  }

  if (!catalog.length) {
    return (
      <Alert variant="warning" className="mb-0 border-0">
        {K.EMPTY_CATALOG}
      </Alert>
    );
  }

  return (
    <Form onSubmit={handleSave} className="nr-care-board">
      <header className="nr-care-board__header">
        <p className="nr-care-board__intro">{K.INTRO}</p>
        <div className="nr-care-board__stats">
          <StatPill label={K.STAT_TOTAL} value={catalog.length} />
          <StatPill label={K.STAT_CHECKED} value={selectedIds.size} tone="active" />
          <StatPill label={K.STAT_SAVED} value={recordedCount} tone="saved" />
          {pendingItems.length > 0 ? (
            <StatPill label={K.STAT_PENDING} value={pendingItems.length} tone="pending" />
          ) : null}
        </div>

        <div className="nr-care-board__filters">
          <div className="nr-care-board__filter-row" role="group" aria-label={K.FILTER_VIEW_ARIA}>
            {[
              { id: 'ALL', label: K.FILTER_ALL },
              { id: 'CHECKED', label: K.FILTER_CHECKED },
              { id: 'PENDING', label: K.FILTER_PENDING }
            ].map((f) => (
              <button
                key={f.id}
                type="button"
                className={`nr-care-chip${viewFilter === f.id ? ' is-active' : ''}`}
                onClick={() => setViewFilter(f.id)}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <div className="nr-care-board__quick">
          <button type="button" className="nr-care-link-btn" onClick={selectAllVisible} disabled={busy}>
            {K.SELECT_ALL_VISIBLE}
          </button>
          <span className="nr-care-board__quick-sep" aria-hidden>
            ·
          </span>
          <button type="button" className="nr-care-link-btn" onClick={clearNewSelection} disabled={busy}>
            {K.CLEAR_NEW}
          </button>
        </div>
      </header>

      <div className="nr-care-board__scroll">
        {visibleItems.length === 0 ? (
          <p className="nr-care-board__empty text-muted small">{K.EMPTY_FILTER}</p>
        ) : (
          <div className="nr-care-grid">
            {visibleItems.map((item) => {
              const key = catalogItemKey(kind, item.id);
              const isChecked = selectedIds.has(key);
              const isRecorded = isItemRecorded(kind, item, careActions, recordedIds);
              const isPending = isChecked && !isRecorded;
              return (
                <button
                  key={key}
                  type="button"
                  className={[
                    'nr-care-card',
                    isChecked ? 'is-checked' : '',
                    isRecorded ? 'is-recorded' : '',
                    isPending ? 'is-pending' : ''
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => toggleItem(key)}
                  disabled={busy}
                  title={item.description || item.name}
                  aria-pressed={isChecked}
                >
                  <span className="nr-care-card__mark" aria-hidden>
                    {isChecked ? <Icon name="checkCircle" size={18} /> : <span className="nr-care-card__box" />}
                  </span>
                  <span className="nr-care-card__body">
                    <span className="nr-care-card__name">{item.name}</span>
                    {kind === 'care' && item.description ? (
                      <span className="nr-care-card__desc">{item.description}</span>
                    ) : null}
                  </span>
                  {isRecorded ? (
                    <span className="nr-care-card__status">{K.RECORDED_BADGE}</span>
                  ) : isPending ? (
                    <span className="nr-care-card__status nr-care-card__status--pending">{K.PENDING_BADGE}</span>
                  ) : null}
                </button>
              );
            })}
          </div>
        )}

        {manualCareActions.length > 0 ? (
          <aside className="nr-care-manual">
            <span className="nr-care-manual__title">{K.MANUAL_TITLE}</span>
            <ul className="nr-care-manual__list">
              {manualCareActions.map((a) => (
                <li key={a.id}>
                  {a.action_type}
                  {a.description ? ` — ${a.description}` : ''}
                </li>
              ))}
            </ul>
          </aside>
        ) : null}
      </div>

      <footer className="nr-care-board__footer">
        <Form.Control
          className="nr-care-board__note"
          value={globalNote}
          onChange={(e) => setGlobalNote(e.target.value)}
          placeholder={K.GLOBAL_NOTE_PLACEHOLDER}
          disabled={busy}
          aria-label={K.GLOBAL_NOTE_LABEL}
        />
        <ClinicalButton
          variant="save"
          type="submit"
          loading={busy}
          icon="checkCircle"
          className="nr-care-board__save"
          disabled={!pendingItems.length && !busy}
        >
          {pendingItems.length > 0
            ? K.SAVE_BUTTON.replace('{n}', String(pendingItems.length))
            : K.SAVE_BUTTON_NONE}
        </ClinicalButton>
      </footer>
    </Form>
  );
}
